'use client'

import { Cloud } from '@carbon/icons-react'

import { Eyebrow, HAIRLINE, useReveal, EASE } from '@/components/page-kit'
import { cn } from '@/lib/utils'

/* Cloud provider rail.

   Logos are svgl.app SVGs served from /public/logos. Only AWS ships a theme
   pair (its wordmark is solid ink, so it needs opposite polarity per theme);
   every other mark is full brand colour and reads on both canvases — verified
   against white and the zinc dark canvas rather than assumed. */

type Provider = {
  name: string
  /** Single brand-colour mark that works on either canvas. */
  src?: string
  /** Marks whose ink is solid and therefore theme-dependent. */
  pair?: { light: string; dark: string }
}

const PROVIDERS: Provider[] = [
  {
    name: 'Amazon Web Services',
    pair: { light: '/logos/aws-light.svg', dark: '/logos/aws-dark.svg' },
  },
  { name: 'Microsoft Azure', src: '/logos/azure.svg' },
  { name: 'Google Cloud', src: '/logos/google-cloud.svg' },
  { name: 'IBM', src: '/logos/ibm.svg' },
  { name: 'DigitalOcean', src: '/logos/digitalocean.svg' },
  { name: 'Cloudflare', src: '/logos/cloudflare.svg' },
]

/* One box for every mark, so a wide wordmark and a square glyph occupy the same
   footprint instead of each being sized by its own aspect ratio. */
const LOGO_BOX = 'h-9 w-full max-w-[132px] object-contain sm:h-10'

export function CloudProviders() {
  const block = useReveal<HTMLDivElement>(0.15)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <div ref={block.ref}>
          <Eyebrow>Providers</Eyebrow>

          <div className="mt-6 flex items-start gap-4">
            <Cloud
              size={32}
              className="mt-1 shrink-0 text-emerald-600 dark:text-primary-active"
            />
            <h2 className="font-heading max-w-3xl text-2xl font-normal leading-[1.3] tracking-tight text-foreground sm:text-3xl lg:text-[40px] lg:leading-[1.25]">
              Built on the major cloud platforms
            </h2>
          </div>
        </div>

        {/* Hairline grid: each cell carries its own right and bottom rule and the
            container closes the top and left, so a row that does not fill the
            grid leaves page background rather than a stray block of tint. */}
        <ul
          className={cn(
            'mt-10 grid grid-cols-2 overflow-hidden rounded-xl border-l border-t sm:grid-cols-3 lg:grid-cols-6',
            HAIRLINE,
            '[&>*]:border-b [&>*]:border-r [&>*]:border-border/70'
          )}
        >
          {PROVIDERS.map((p, i) => (
            <li
              key={p.name}
              className="flex items-center justify-center px-5 py-7 sm:px-6 sm:py-8"
              style={{
                opacity: block.shown ? 1 : 0,
                transform: block.shown ? 'none' : 'translateY(10px)',
                transition: `opacity 600ms ${EASE} ${i * 60}ms, transform 600ms ${EASE} ${i * 60}ms`,
              }}
            >
              {p.pair ? (
                <>
                  <img
                    src={p.pair.light}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className={cn(LOGO_BOX, 'dark:hidden')}
                  />
                  <img
                    src={p.pair.dark}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className={cn(LOGO_BOX, 'hidden dark:block')}
                  />
                </>
              ) : (
                <img
                  src={p.src}
                  alt={p.name}
                  loading="lazy"
                  decoding="async"
                  className={LOGO_BOX}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
